/**
 * Product rules for practitioner role transitions.
 * See docs/prd/users/PRD_ROLE_TRANSITIONS.md and @unify/fhir-model/role-display.
 */

import type { Role } from '@/pages/users/types/rbac';
import {
  holdsSupervisingCapability,
  holdsTreatingCapability,
  normalizeHeldRoles,
  removeDisplayRoleFromHeld,
} from '@unify/fhir-model/role-display';

export const CLINICAL_ROLES = new Set<Role>(['provider', 'supervisor']);

export function isClinicalRole(role: Role): boolean {
  return CLINICAL_ROLES.has(role);
}

/** @deprecated Prefer roleSetChangeNeedsTransitionWizard for multi-role set deltas. */
export function roleChangeNeedsTransitionWizard(
  currentRole: Role,
  targetRole: Role,
): boolean {
  if (currentRole === targetRole) return false;
  return isClinicalRole(currentRole) || isClinicalRole(targetRole);
}

/**
 * Whether removing `roleToRemove` from the held set would leave the user with no roles.
 * That path is not a role transition — use deactivate instead.
 */
export function removalWouldLeaveNoRoles(
  heldNow: readonly string[],
  roleToRemove: string,
): boolean {
  return removeDisplayRoleFromHeld(heldNow, roleToRemove).length === 0;
}

/**
 * Whether changing from heldNow → heldNext requires the orphan closeout wizard.
 * Empty target sets are invalid (use deactivate) and do not open the wizard.
 */
export function roleSetChangeNeedsTransitionWizard(
  heldNow: readonly string[],
  heldNext: readonly string[],
): boolean {
  const now = normalizeHeldRoles(heldNow);
  const next = normalizeHeldRoles(heldNext);
  if (next.length === 0) return false;
  return (
    (holdsTreatingCapability(now) && !holdsTreatingCapability(next))
    || (holdsSupervisingCapability(now) && !holdsSupervisingCapability(next))
  );
}

/**
 * Which capabilities the practitioner is giving up, judged against the whole held set.
 * Closeout must not be inferred from the derived primary alone.
 */
export function capabilityLossFromBaseline(input: {
  baselineHeld: readonly string[];
  treatsClients: boolean;
  supervises: boolean;
}): { leavingTreating: boolean; leavingSupervising: boolean } {
  const held = normalizeHeldRoles(input.baselineHeld);
  return {
    leavingTreating: !input.treatsClients && holdsTreatingCapability(held),
    leavingSupervising: !input.supervises && holdsSupervisingCapability(held),
  };
}

/**
 * Closeout rows still missing a replacement. Transfers and facilitator replacements
 * cannot be applied without one, so the wizard blocks until this reaches zero.
 */
export function countUnresolvedReplacements(
  rows: readonly { requiresReplacement: boolean; replacementId?: string }[],
): number {
  return rows.filter((row) => row.requiresReplacement && !row.replacementId).length;
}

/** Held set for replacement pickers — login default `role` can hide stacked supervisor/provider. */
function heldRolesForReplacement(user: {
  role: string;
  heldRoles?: readonly string[];
}): readonly string[] {
  return user.heldRoles?.length ? user.heldRoles : [user.role];
}

export function isEligibleReplacementSupervisor(
  candidate: { id: string; role: string; heldRoles?: readonly string[] },
  leavingPractitionerId: string,
): boolean {
  return candidate.id !== leavingPractitionerId
    && holdsSupervisingCapability(heldRolesForReplacement(candidate));
}

export function isEligibleReplacementProvider(
  candidate: { id: string; role: string; heldRoles?: readonly string[] },
  leavingPractitionerId: string,
): boolean {
  return candidate.id !== leavingPractitionerId
    && holdsTreatingCapability(heldRolesForReplacement(candidate));
}

export function closeoutRequiredMessage(impact: {
  activeClients: number;
  futureAppointments: number;
  supervisees: number;
  pendingCosignatures: number;
}): string | null {
  const parts: string[] = [];
  if (impact.activeClients > 0) {
    parts.push(
      `${impact.activeClients} active client episode(s) must be transferred or discharged before this role change can complete`,
    );
  }
  if (impact.futureAppointments > 0) {
    parts.push(
      `${impact.futureAppointments} future appointment(s) must be transferred, cancelled, or queued for reschedule`,
    );
  }
  if (impact.supervisees > 0) {
    parts.push(
      `${impact.supervisees} supervisee(s) must be reassigned to another supervisor`,
    );
  }
  if (impact.pendingCosignatures > 0) {
    parts.push(
      `${impact.pendingCosignatures} pending cosignature / review item(s) must be reassigned with the supervisee`,
    );
  }
  if (parts.length === 0) return null;
  return parts.join('. ') + '.';
}
